import { createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { StudentSidebar } from "@/components/student/dashboard/StudentSidebar";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useStudentProfile } from "@/contexts/StudentProfileContext";
import { useMyInquiries } from "@/hooks/use-inquiries";
import { cn } from "@/lib/utils";

const StudentLayoutSearchContext = createContext<(() => void) | null>(null);

/**
 * Navigates to the default (one-to-one) offering search. Kept as a context hook so
 * existing callers (e.g. an empty-state "Find teachers" button) work unchanged after
 * the search moved from an embedded view to dedicated per-type routes (ADR-088).
 */
export function useOpenFindTeachers() {
  return useContext(StudentLayoutSearchContext);
}

interface StudentLayoutProps {
  children: React.ReactNode;
  /** Extra classes applied to the flex-1 content wrapper (e.g. overflow-hidden, bg-white) */
  contentClassName?: string;
  /** Inline style for the content wrapper (e.g. fixed height for chat views) */
  contentStyle?: React.CSSProperties;
}

export function StudentLayout({ children, contentClassName, contentStyle }: StudentLayoutProps) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { profile } = useStudentProfile();
  const { data: inquiriesData } = useMyInquiries(0, 10);

  const studentName = profile?.aboutMe?.fullName || user?.name || "Student";
  const threads = inquiriesData?.content ?? [];
  const unreadCount = threads.reduce((sum, t) => sum + t.unreadCount, 0);

  return (
    <div className="min-h-screen bg-background student-theme">
      <SidebarProvider>
        <DashboardTopNav role="student" />
        <div className="min-h-screen flex w-full pt-14">
          <StudentSidebar
            studentName={studentName}
            studentAvatar=""
            unreadInquiries={unreadCount}
          />
          <div
            className={cn("flex-1 flex flex-col min-w-0", contentClassName)}
            style={contentStyle}
          >
            <StudentLayoutSearchContext.Provider value={() => navigate("/search/one-to-one")}>
              {children}
            </StudentLayoutSearchContext.Provider>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
